import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import arrowSVG from '../Iconos/arrow.svg';
import { Helmet } from 'react-helmet';
import './AvatarPrompt.css';
import AvatarPreviewImage from './AvatarPreviewImage';

// Imágenes de ejemplo
const exampleImages = [
  'https://hihaho.s3.amazonaws.com/ExampleImages/hombre2.png',
  'https://hihaho.s3.amazonaws.com/ExampleImages/noelle.jpeg',
  'https://hihaho.s3.amazonaws.com/ExampleImages/hombre.png',
  'https://hihaho.s3.amazonaws.com/ExampleImages/captura.png',
];

const VALID_MINUTES = 35; // Las imágenes se guardan por 35 minutos

const AvatarPrompt: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isProcessStarted, setIsProcessStarted] = useState(false);

  useEffect(() => {
    const imageDataJSON = localStorage.getItem('avatarData');
    if (imageDataJSON) {
      try {
        const imageData = JSON.parse(imageDataJSON);
        const timeNow = new Date().getTime();
        const timeElapsed = (timeNow - imageData.timestamp) / 1000 / 60; // Tiempo en minutos

        if (timeElapsed < VALID_MINUTES) {
          setSelectedImage(imageData.imageUrl);
        } else {
          localStorage.removeItem('avatarData'); // Imágenes caducadas
        }
      } catch (error) {
        console.error('Error al analizar avatarData del localStorage:', error);
      }
    }
  }, []);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append('image', file);

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
        const response = await axios.post(`${backendUrl}/upload-avatar-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const serverImageUrl = response.data.imageUrl;
        setSelectedImage(serverImageUrl); // Establecer la URL de previsualización
        const imageData = { timestamp: new Date().getTime(), imageUrl: serverImageUrl };
        localStorage.setItem('avatarData', JSON.stringify(imageData));
      } catch (error) {
        console.error('Error al subir la imagen:', error);
      }
    }
  };

  const handleSelectExampleImage = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    const imageData = {
      timestamp: new Date().getTime(),
      imageUrl: imageUrl,
    };
    localStorage.setItem('avatarData', JSON.stringify(imageData));
  };

  const handleNextClick = async () => {
    setIsGenerating(true);
    setIsProcessStarted(true); // Marcar que el proceso ha comenzado

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('No hay token de autenticación disponible');
      }

      // Restamos los créditos (35 créditos en este caso)
      console.log("Iniciando proceso para restar créditos...");

      const creditosResponse = await axios.post(`${backendUrl}/restar-creditos`, {
        cantidad: 35
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('Respuesta de restar créditos:', creditosResponse.data); // Verificar respuesta

      if (creditosResponse.data.success) {
        console.log('Créditos restados correctamente. Procediendo con el siguiente paso...');
        // Aquí puedes manejar lo que ocurra después, como navegar a otra página
        history.push('/Social-Networking-Avatar2');
      } else {
        console.error('Problema al restar créditos:', creditosResponse.data.mensaje);
        alert(creditosResponse.data.mensaje);
      }
    } catch (error) {
      console.error("Error al restar créditos:", error);
      alert('Ha ocurrido un error al restar créditos.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className='container-avatar'>
      <Helmet>
        <title>{t('avatar.createTitle')}</title>
      </Helmet>
      <div className="image-prompt-description">
        <div className="image-prompt-title">
          <p>{t('Elige tu imagen')} <span className="highlight">{t('imagePrompt.ImageHere')}</span>.</p>
        </div>
        <div className="upload-instructions">
          <p>Sube tu propia imagen con un fondo blanco y asegúrate de que tu rostro esté bien iluminado. El nombre del archivo de la imagen no puede tener espacios.</p>
        </div>
      </div>
      <div className='avatar-container'>
        {/* Aquí mostramos las imágenes de ejemplo */}
        <AvatarPreviewImage thumbnails={exampleImages} onSelect={handleSelectExampleImage} />
        <div className='avatar-prompt-container'>
          <div className='main-image-container'>
            <div className='image-upload-container'>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                id="upload-image"
              />
              <label htmlFor="upload-image" className="upload-label">
                {selectedImage ? (
                  <img src={selectedImage} alt="Selected" className="selected-image" />
                ) : (
                  <div className="file-upload-label">Seleccionar imagen</div>
                )}
              </label>
            </div>

            <div className='image-selection-navigation'>
              <button className='image-selection-previous' onClick={() => history.push('/')}>
                <img src={arrowSVG} alt='' className='reverse-arrow' /> {t('Anterior')}
              </button>
              <button className='image-selection-next' onClick={handleNextClick}>
                {t('Siguiente')} <img src={arrowSVG} alt='' className='normal-arrow' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarPrompt;
